import { ANumberAssignment } from "@/models/anumberassignments";
import { ANumberGroup } from "@/models/anumbergroup";
import { OutboundResponseError } from "@/models/outboundresponserror";
import { ModelValidation } from "./modelvalidation";
import BatchForm from '../components/batch/batchform.vue';

export class Batch extends ModelValidation {
    private maxBatchSize = 25000;

    public step = 1;
    public destinations: any[] = [];
    public ttlSeconds = null as string | null;
    public customText = null as string | null;
    public response = null as any;
    public aNumbers = [] as ANumberAssignment[];
    public aNumberGroups = [] as ANumberGroup[];
    public aNumberGroupSelected = null as ANumberGroup | null;
    public keepAfterCall = false as boolean;
    public programPushType = null as string | null;

    public static empty(): Batch {
        return new Batch();
    }

    public toIAsyncBatchRequests(): IAsyncBatchRequest[] {
        if (this.destinations.length === 0) {
            throw new Error("Unable to generate request with no destinations");
        } else if (this.aNumbers.length === 0 && this.aNumberGroupSelected === null) {
            throw new Error("Unable to generate request with no aNumbers");
        }

        const allDestinations = [] as IAsyncBatchRequestDestination[][];
        let thisDestinations = [] as IAsyncBatchRequestDestination[];
        let count = 0;
        for (const destination of this.destinations) {
            if (destination.customTrackingId) {
                thisDestinations.push({
                    bNumber: destination.bNumber,
                    customTrackingId: destination.customTrackingId,
                });
            } else if(this.customText) {
                thisDestinations.push({
                    bNumber: destination.bNumber,
                    personalization: { "MY_VAR": this.customText }
                });
            } else {
                thisDestinations.push({
                    bNumber: destination.bNumber
                });
            }

            count++;

            if (thisDestinations.length === this.maxBatchSize) {
                allDestinations.push(thisDestinations);
                thisDestinations = [];
                count = 0;
            }
        }

        // clean up remaining
        if (thisDestinations.length > 0) {
            allDestinations.push(thisDestinations);
        }

        const requestANumbers = this.aNumbers.map((x) => x.aNumber!);

        if (this.programPushType === "inform") {
            // Inform Batch
            const result = [] as IAsyncInformBatchRequest[];

            for (const destinationSet of allDestinations) {
                const request: IAsyncInformBatchRequest = {
                    aNumbers: requestANumbers,
                    destinations: destinationSet,
                    programPushType: "Inform",
                };

                if (this.ttlSeconds !== null) {
                    request.ttlSeconds = parseInt(this.ttlSeconds, 10) * 60;
                }
                console.log(request.ttlSeconds);
                result.push(request);
            }

            return result;
        } else {
            // Engage Batch
            const result = [] as IAsyncBatchRequest[];

            for (const destinationSet of allDestinations) {
                const request: IAsyncBatchRequest = {
                    destinations: destinationSet,
                    keepAfterCall: this.keepAfterCall,
                };

                if (this.aNumberGroupSelected) {
                    request.aNumberGroupId = this.aNumberGroupSelected.uuid;
                } else {
                    request.aNumbers = requestANumbers;
                }

                if (this.ttlSeconds !== null) {
                    request.ttlSeconds = parseInt(this.ttlSeconds, 10) * 60;
                    console.log(request.ttlSeconds);
                }

                result.push(request);
            }

            return result;
        }
    }
}

export class BatchDestination {
    public constructor(public phoneNumber: string) {}
}

export interface IAsyncBatchRequestDestination {
    bNumber: string;
    customTrackingId?: string;
    personalization?: any | null;
}

export interface IAsyncBatchRequest {
    continuationToken?: string;
    ttlSeconds?: number | 16;
    keepAfterCall?: boolean | null;
    programPushType?: string | null;
    aNumbers?: any[];
    destinations: IAsyncBatchRequestDestination[];
    aNumberGroupId?: string | null;
}

export interface IAsyncInformBatchRequest {
    continuationToken?: string;
    ttlSeconds?: number;
    programPushType?: string | null;
    aNumbers: any[];
    destinations: IAsyncBatchRequestDestination[];
}

export interface IAsyncBatchResponse {
    batchId: string;
    trackingIds: string[];
    continuationToken: string;
}

export class BatchResponseModel {
    public isAborted = false;
    public response = null as any;
    public responseCode!: OutboundResponseError | null;

    public get isPushProcessing(): boolean {
        if (this.isAborted) {
            return false;
        }
        if (this.isPushError) {
            return false;
        }

        return this.response === null;
    }

    public get isPushSuccess(): boolean {
        return this.response !== null && !this.response.isAxiosError;
    }

    public get isPushError(): boolean {
        if (this.isAborted) {
            return true;
        }
        return this.response !== null && this.response.isAxiosError;
    }

    public get pushError(): string {
        if (this.isAborted) {
            return "Request aborted";
        } else {
            var error = this.response.response.data.responseCode;
            this.responseCode = OutboundResponseError.convert(error || null);
            return this.responseCode!.display;
        }
    }

    public constructor(
        public request: IAsyncBatchRequest,
        public requestNumber: number
    ) {}
}
